import React, { Fragment, useEffect, useRef, useState } from 'react';
import Principal from '@app/model/authentication/Principal';
import useSiteState from '@app/state/SiteState';
import { Navigate } from 'react-router-dom';
import MockDashboard from '@app/site/system/page/MockDashboard';
import { Transition } from '@headlessui/react';
import { Cog6ToothIcon } from '@heroicons/react/20/solid';
import Utilities from '@util/Utilities';
import useExecuteRegistrationEnrichHotelsHook
	from '@app/site/registration/component/ExecuteRegistrationEnrichHotelsHook';
import useExecuteRegistrationRankHotelsHook from '@app/site/registration/component/ExecuteRegistrationRankHotelsHook';
import useExecuteRegistrationMarketStateUpdateHook
	from '@app/site/registration/component/ExecuteRegistrationMarketStateUpdateHook';
import RegistrationState from '@app/model/authentication/RegistrationState';
import { SlackService } from '@app/model/slack/SlackService';
import AuthProvider from '@app/model/authentication/AuthProvider';
import CompanyHotelService from '@app/model/company_hotel/service/CompanyHotelService';
import AuthorizationService from '@app/model/authentication/service/AuthorizationService';
import {
	useLatestProcessRequestExecutionStateMonitor,
} from '@app/site/secured/rateshopper/rates/provider/useLatestProcessRequestExecutionStateMonitor';

const ExecuteAccountSetupFlowPage: React.FunctionComponent<{ onComplete: (principal: Principal) => void }>
	= ({ onComplete }) => {

	const principal = useSiteState(state => state.principal);

	if (!!principal)
		return <ExecuteAccountSetupFlow principal={principal} onComplete={onComplete} />;
	else return <Navigate to="/sign-in" replace />;
};

const ExecuteAccountSetupFlow: React.FunctionComponent<{
	principal: Principal,
	onComplete: (principal: Principal) => void
}>
	= ({ principal, onComplete }) => {

	const [_principal, setPrincipal] = useState<Principal>(principal);
	const [percentage, setPercentage] = useState<number>(10);
	const [stepPercentage, setStepPercentage] = useState<number>(10);
	const [hasError, setHasError] = useState<boolean>(false);
	const [title, setTitle] = useState<string>('Account Setup');
	const [description, setDescription] = useState<React.ReactNode>(<p />);
	const startedRegistrationStateRef = useRef<RegistrationState | null>(null);

	const executeRegistrationEnrichHotelsHook = useExecuteRegistrationEnrichHotelsHook();
	const executeRegistrationRankHotelsHook = useExecuteRegistrationRankHotelsHook();
	const executeRegistrationMarketStateUpdateHook = useExecuteRegistrationMarketStateUpdateHook();

	const monitor = useLatestProcessRequestExecutionStateMonitor();
	const hasActiveProcess = !!monitor.latestActiveExecutionState && !monitor.latestActiveExecutionState.isFinished;


	useEffect(() => {
		if (hasActiveProcess) return;
		if (startedRegistrationStateRef.current === _principal.registrationState) return;

		switch (_principal.registrationState) {
			case RegistrationState.PENDING_HOTELS_ENRICH: {
				startedRegistrationStateRef.current = _principal.registrationState;
				executeRegistrationEnrichHotelsHook.start().then((state) => {
					monitor.setLatestActiveExecutionState(state);
					setStepPercentage(10);
					setPercentage(10);
					setTitle('1. Enriching Hotels Information');
					setDescription(
						<p>Finding more information for your hotels.</p>,
					);
				});
				break;
			}
			case RegistrationState.PENDING_HOTELS_RANKING: {
				startedRegistrationStateRef.current = _principal.registrationState;
				executeRegistrationRankHotelsHook.start().then((state) => {
					monitor.setLatestActiveExecutionState(state);
					setStepPercentage(40);
					setPercentage(40);
					setTitle('2. Ranking Hotel Rooms');
					setDescription(
						<p>Categorizing hotel rooms to the proper bucket.</p>,
					);
				});
				break;
			}
			case RegistrationState.PENDING_UPDATE_MARKET_STATE: {
				startedRegistrationStateRef.current = _principal.registrationState;
				executeRegistrationMarketStateUpdateHook.start().then((state) => {
					monitor.setLatestActiveExecutionState(state);
					setStepPercentage(70);
					setPercentage(70);
					setTitle('3. Initializing Market State');
					setDescription(
						<p>Finding the hotels best prices for current month.</p>,
					);
				});
				break;
			}
			case RegistrationState.COMPLETED: {
				startedRegistrationStateRef.current = _principal.registrationState;
				setPercentage(100);
				notifyForRevHeroRegistration(_principal);
				onComplete(_principal);
			}
		}
	}, [_principal, hasActiveProcess]);

	useEffect(() => {
		if (executeRegistrationEnrichHotelsHook.isFinished) {
			AuthorizationService.findCurrentPrincipal().then(setPrincipal);
		} else if (executeRegistrationRankHotelsHook.isFinished) {
			AuthorizationService.findCurrentPrincipal().then(setPrincipal);
		} else if (executeRegistrationMarketStateUpdateHook.isFinished) {
			executeRegistrationMarketStateUpdateHook.finish().then(setPrincipal);
		}

		if (!!monitor && monitor.latestActiveExecutionState && monitor.latestActiveExecutionState.statistics.taskStatistics.total > 0)
			setPercentage(Math.ceil(stepPercentage + (monitor.latestActiveExecutionState.statistics.taskStatistics.ratio * 30)));
	}, [monitor.latestActiveExecutionState]);

	useEffect(() => {
		if (monitor.latestActiveExecutionState?.hasFailed) setHasError(true);
	}, [monitor.latestActiveExecutionState]);

	const notifyForRevHeroRegistration = (principal: Principal) => {
		if (principal.authProviders.some(authProvider => authProvider === AuthProvider.REVHERO)) {
			CompanyHotelService.findCompanyHotels().then(companyHotels => {
				SlackService.send(SlackService.CHANNEL_REVHERO_REGISTRATION, `A new profile with username: ${principal!.username} has been created with company id: ${principal!.companyId} 
				and hotelId(s): ${companyHotels.map(companyHotel => companyHotel.mainHotel.competitors[0].hotel.id).join(', ')} 
				and companyHotelId(s): ${companyHotels.map(companyHotel => companyHotel.id).join(', ')} 
				has completed registration`);
			});
		}
	};


	return <>
		<MockDashboard />
		<Transition
			show={true}
			as={Fragment}
			enter="transform ease-out duration-300 transition"
			enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
			enterTo="translate-y-0 opacity-100 sm:translate-x-0"
			leave="transition ease-in duration-100"
			leaveFrom="opacity-100"
			leaveTo="opacity-0"
		>
			<div className="z-10">
				<div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
				<div className="pointer-events-none fixed inset-0 flex items-center justify-center px-4 py-6 sm:p-6">
					<div className="flex w-full flex-col items-center justify-center space-y-4 z-10">
						{!hasError && <div
							className="pointer-events-auto relative w-full max-w-sm overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5">
							<div className="p-4">
								<div
									className="mx-auto mt-4 flex h-12 w-12 items-center justify-center rounded-full bg-blue-100">
									<Cog6ToothIcon className="animate-spin h-6 w-6 text-blue-600" />
								</div>
								<div className="mt-4 text-center">
									<h3 className="text-base font-semibold leading-6 text-gray-900">
										{title}
									</h3>
									<div className="mt-1 text-sm text-gray-500">
										{description}
									</div>
								</div>
								<div className="w-full mt-4">
									<div className="flex items-center justify-between gap-4 mb-1">
										<h6
											className="block font-sans text-sm antialiased font-semibold leading-relaxed tracking-normal text-blue-gray-900">Completed</h6>
										<h6
											className="block font-sans text-sm antialiased font-semibold leading-relaxed tracking-normal text-blue-gray-900">{Utilities.formatDouble(percentage, 0)}%</h6>
									</div>
									<div
										className="flex-start flex h-2 w-full overflow-hidden rounded-full bg-gray-100 font-sans text-xs font-medium">
										<div
											className="flex items-center justify-center h-full overflow-hidden text-white break-all bg-blue-600 rounded-full"
											style={{ width: percentage + '%' }} />
									</div>
								</div>
							</div>
						</div>}
					</div>
				</div>
			</div>
		</Transition>
	</>;

};

export default ExecuteAccountSetupFlowPage;
